import React, { FC, useCallback, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import ProjectPageShell, { Crumb } from "../shared/ProjectPageShell";
import TaskInDetailView from "./TaskInDetailView";
import TaskCreation from "./TaskCreation";
import { RootState } from "../../../store/store";
import {
  updateTaskListDetailsModalSlice,
  resetTaskListDetailsModalSlice,
} from "../../../store/slices/projectManagement/task-list-view/TaskListDetailModalSlice";
import {
  updateActivityInDetailModalSlice,
  resetActivityInDetailModalSlice,
} from "../../../store/slices/projectManagement/activity-view/ActivityInDetailView";
import { tasksApis } from "../../../api/NewTasks";
import { useWbsName } from "../shared/useWbsCodes";

const TaskDetailPage: FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wbsName = useWbsName();

  const [loading, setLoading] = useState(true);
  const [taskData, setTaskData] = useState<any>(null);

  // Get data from location state
  const locationState = location.state as any;
  const projectId = locationState?.projectId || locationState?.id;
  const projectName = locationState?.projectName;
  const phaseId = locationState?.phaseId;
  const phaseName = locationState?.phaseName;
  const activityId = locationState?.activityId;
  const activityName = locationState?.activityName;
  const fromWorkApprovals = locationState?.fromWorkApprovals;
  const derivedProjectStatus =
    locationState?.extra?.screenName || "Active";
  // Generic return path - allows navigation back to any source page
  const returnPath = locationState?.returnPath;
  const returnState = locationState?.returnState;

  const { data: TaskListInDetailModal } = useSelector(
    (state: RootState) => state.TaskListInDetailModal
  );

  // Get projectId from Redux layout state as fallback
  const { currentProjectId, currentProjectName } = useSelector(
    (state: RootState) => state.layout
  );

  // Use Redux projectId as fallback when location state doesn't have it
  const resolvedProjectId = projectId || currentProjectId;
  const resolvedProjectName = projectName || currentProjectName;

  const fetchTaskDetails = useCallback(
    async (showSpinner: boolean = true) => {
      if (!taskId) return;
      if (showSpinner) setLoading(true);
      try {
        const response = await tasksApis.fetchTaskById(
          Number(taskId),
          derivedProjectStatus
        );
        if (response?.status && response?.data) {
          const task = response.data;
          setTaskData(task);

          const projectCtx = {
            id: task.projectId || resolvedProjectId,
            name: resolvedProjectName || task.projectName || "Project",
            status: task.projectStatus || "Active",
          };
          const phaseCtx = {
            id: task.phaseId || phaseId,
            name: phaseName || task.phaseName || "Phase",
            status: task.phaseStatus || "Active",
          };
          const activityCtx = {
            id: task.activityId || activityId,
            name: activityName || task.activityName || "Activity",
            status: task.activityStatus || "Active",
          };

          dispatch(
            updateTaskListDetailsModalSlice({
              open: true,
              id: task.taskId,
              allowEdit: true,
              allowAdd: true,
              projectResponse: projectCtx,
              phaseResponse: phaseCtx,
              activityResponse: activityCtx,
              selectedTaskDetails: task,
              extra: {
                status: task.taskStatus,
                startDate: task.taskPlannedStartDate,
                endDate: task.taskPlannedEndDate,
                ownerName: task.taskOwnerName,
                createdAt: task.createdAt,
                createdByUserName: task.createdByUserName,
              },
            })
          );

          // TaskCreation (subtask flow) reads project / phase / activity context
          // from ActivityInDetailModal, so mirror the same data there. Without
          // this, opening "Add Subtask" results in the modal title showing an
          // empty project name and `/users/null`, `/fetchProjectTags/null` API
          // calls.
          dispatch(
            updateActivityInDetailModalSlice({
              id: task.activityId || activityId,
              open: true,
              activityName: activityName || task.activityName || "",
              projectResponse: projectCtx,
              phaseResponse: {
                ...phaseCtx,
                startDate: "",
                endDate: "",
              },
              extra: {
                status: task.activityStatus || "Active",
                startDate: task.activityStartDate,
                endDate: task.activityEndDate,
              },
              allowEdit: true,
              allowAdd: true,
            })
          );
        }
      } catch (error) {
        console.error("Error fetching task details:", error);
      } finally {
        if (showSpinner) setLoading(false);
      }
    },
    [
      taskId,
      dispatch,
      resolvedProjectId,
      resolvedProjectName,
      phaseId,
      phaseName,
      activityId,
      activityName,
    ]
  );

  useEffect(() => {
    fetchTaskDetails();
  }, [fetchTaskDetails]);

  useEffect(() => {
    return () => {
      dispatch(resetActivityInDetailModalSlice());
      dispatch(resetTaskListDetailsModalSlice());
    };
  }, [dispatch]);

  // Navigation handlers
  const handleBack = () => {
    dispatch(resetTaskListDetailsModalSlice());
    // Use returnPath if available (for navigation from WBS, Reports, etc.)
    if (returnPath) {
      navigate(returnPath, { state: returnState });
    } else if (fromWorkApprovals) {
      navigate(`/pms/project/workapprovals/${resolvedProjectId}`, {
        state: {
          id: resolvedProjectId,
          projectName: resolvedProjectName,
        }
      });
    } else {
      navigate(`/pms/project/tasks/${resolvedProjectId}`, {
        state: {
          ...locationState,
          id: resolvedProjectId,
          projectName: resolvedProjectName,
        }
      });
    }
  };

  const handleBackToTasks = () => {
    dispatch(resetTaskListDetailsModalSlice());
    navigate(`/pms/project/tasks/${resolvedProjectId}`, {
      state: {
        ...locationState,
        id: resolvedProjectId,
        projectName: resolvedProjectName,
      }
    });
  };

  const handleBackToActivity = () => {
    dispatch(resetTaskListDetailsModalSlice());
    if (activityId) {
      navigate(`/pms/project/activity-detail/${activityId}`, {
        state: { ...locationState, projectId: resolvedProjectId, projectName: resolvedProjectName, phaseId, phaseName },
      });
    } else {
      navigate(`/pms/project/tasks/${resolvedProjectId}`, { state: locationState });
    }
  };

  const handleBackToPhase = () => {
    dispatch(resetTaskListDetailsModalSlice());
    navigate(`/pms/project/phase-detail/${phaseId}`, {
      state: { ...locationState, projectId: resolvedProjectId, projectName: resolvedProjectName },
    });
  };

  const handleBackToPhases = () => {
    dispatch(resetTaskListDetailsModalSlice());
    navigate(`/pms/project/phases/${resolvedProjectId}`, { state: locationState });
  };

  const handleBackToProject = () => {
    dispatch(resetTaskListDetailsModalSlice());
    navigate(`/pms/project/dashboard/${resolvedProjectId}`, { state: locationState });
  };

  const handleClose = () => {
    handleBackToActivity();
  };

  const taskName = taskData?.taskName || TaskListInDetailModal?.selectedTaskDetails?.taskName || "Task";

  const breadcrumb: Crumb[] = [
    { label: resolvedProjectName || "Project", onClick: handleBackToProject },
    ...(phaseName
      ? [
          {
            label: wbsName(phaseName),
            ...(phaseId ? { onClick: handleBackToPhase } : {}),
          } as Crumb,
        ]
      : []),
    ...(activityName
      ? [
          {
            label: wbsName(activityName),
            ...(activityId ? { onClick: handleBackToActivity } : {}),
          } as Crumb,
        ]
      : []),
    { label: taskName },
  ];

  return (
    <>
      {/* Modals stay siblings of the shell so `loading` can't unmount them */}
      {TaskListInDetailModal?.showTaskCreationModal &&
        TaskListInDetailModal?.isSubtask && (
          <TaskCreation refreshData={() => {}} />
        )}
      <ProjectPageShell
        headTitle={`${taskName} - Task Details`}
        breadcrumb={breadcrumb}
        title={taskName}
        eyebrow="Task"
        onBack={handleBack}
        loading={loading}
      >
        <TaskInDetailView
          id={Number(taskId)}
          activeTab={locationState?.activeTab || "view"}
          refreshData={() => fetchTaskDetails(false)}
          handleClose={handleClose}
        />
      </ProjectPageShell>
    </>
  );
};

export default TaskDetailPage;
